import { Link } from "react-router-dom";
import { useEffect } from "react";
import JsonLd from "@/components/base/JsonLd";
import { articlesMeta, buildArticleJsonLd } from "@/mocks/seoMetadata";

const article = articlesMeta.find((a) => a.slug === "/articoli/ottimizzazione-itaobuy")!;

export default function OttimizzazionePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleJsonLd(article)} />
      <header className="w-full px-6 md:px-10 lg:px-16 py-5 bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-2xl font-bold text-accent-500 tracking-tight">ItaoBuy</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-accent-600">
              itaobuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-primary-600">
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
          </div>
        </div>
      </header>

      <article className="w-full px-6 md:px-10 lg:px-16 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-400 whitespace-nowrap">Ottimizzazione ItaoBuy Spreadsheet</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">Tips & Tricks</span>
              <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">Produttività</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              10 Trucchi per Ottimizzare il Tuo ItaoBuy Spreadsheet
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              Scopri i segreti degli utenti esperti: scorciatoie, formule nascoste, formattazione condizionale e trucchi di produttività per sfruttare al massimo il tuo <strong>ItaoBuy Spreadsheet</strong>.
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>Aggiornato: Luglio 2026</span>
              <span>|</span>
              <span>Tempo di lettura: 7 minuti</span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=Modern%20ultra%20productive%20workspace%20with%20laptop%20displaying%20colorful%20spreadsheet%20with%20conditional%20formatting%20and%20advanced%20formulas%2C%20keyboard%20shortcuts%20overlay%20visible%2C%20minimal%20warm%20cream%20desk%20setup%2C%20soft%20natural%20lighting%2C%20professional%20productivity%20photography%2C%20clean%20inspiring%20composition&width=1200&height=600&seq=article-tips-hero&orientation=landscape" alt="10 Trucchi Ottimizzazione ItaoBuy Spreadsheet" title="Ottimizzazione ItaoBuy Spreadsheet - Trucchi e Scorciatoie Esperti" className="w-full h-auto object-cover" />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Massimizza la Produttività del Tuo ItaoBuy Spreadsheet</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Anche i tool più potenti diventano inutili se non li usi in modo efficiente. Questi 10 trucchi, raccolti dagli utenti più esperti di <strong>ItaoBuy Spreadsheet</strong>, ti aiuteranno a risparmiare tempo, ridurre errori e ottenere risultati migliori con meno sforzo.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Alcuni di questi trucchi sfruttano funzionalità native di Google Sheets, altri richiedono una conoscenza più approfondita delle formule. Tutti sono applicabili immediatamente al tuo foglio esistente.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">I 10 Trucchi degli Esperti</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">1</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Formattazione Condizionale Intelligente</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Colora automaticamente le celle in base ai valori: rosso per prezzi sopra il budget, verde per ordini consegnati, giallo per prodotti in attesa. La formattazione condizionale rende il tuo <strong>ItaoBuy Spreadsheet</strong> immediatamente leggibile a colpo d&apos;occhio.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">2</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Filtri e Viste Personalizzate</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Crea viste filtrate per ogni membro del team o per ogni fase del processo. Una vista mostra solo gli ordini in spedizione, un&apos;altra solo i prodotti da acquistare. Nessuno deve più scorrere migliaia di righe per trovare i dati pertinenti.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">3</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Convalida Dati e Menu a Discesa</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Impedisci errori di battitura creando menu a discesa con valori predefiniti per stati ordine, categorie prodotto e fornitori. La convalida dati garantisce che il tuo <strong>ItaoBuy Spreadsheet</strong> rimanga coerente nel tempo.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">4</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Scorciatoie da Tastiera</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Padroneggia le scorciatoie di Google Sheets: Ctrl+Shift+End per selezionare tutto, Ctrl+D per duplicare righe, Ctrl+K per inserire link rapidi. Ogni scorciatoia risparmia secondi che si accumulano in ore ogni mese.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">5</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Formule ARRAYFORMULA</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Utilizza <code className="px-1.5 py-0.5 bg-background-100 rounded text-xs text-accent-600">ARRAYFORMULA</code> per applicare calcoli a intere colonne senza trascinare formule. Calcola automaticamente totali, medie e percentuali per tutti i prodotti del tuo <strong>ItaoBuy Spreadsheet</strong> in un&apos;unica formula.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10 p-6 md:p-8 bg-accent-500 rounded-2xl text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Diventa un Esperto di ItaoBuy Spreadsheet</h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              Inizia subito a utilizzare <strong className="text-white">ItaoBuy Spreadsheet</strong> e applica questi trucchi per diventare un vero professionista della gestione prodotti.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-6 py-3 bg-white text-accent-500 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/90">
                Visita itaobuy sheet <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
              </a>
              <Link to="/" className="flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/25">
                Torna alla Home <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              </Link>
            </div>
          </section>

          <nav className="flex items-center justify-between pt-6 border-t border-background-200/70">
            <Link to="/" className="flex items-center gap-2 text-sm text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span> ItaoBuy Spreadsheet Home
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-400">Condividi:</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su X"><i className="ri-twitter-x-line text-sm"></i></a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su Facebook"><i className="ri-facebook-fill text-sm"></i></a>
            </div>
          </nav>
        </div>
      </article>

      <footer className="w-full px-4 md:px-8 pt-4 pb-6 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500">&copy; {new Date().getFullYear()} ItaoBuy Spreadsheet Italia. <Link to="/" className="text-accent-500 hover:text-accent-600 transition-colors cursor-pointer ml-1">Torna alla Home</Link></p>
        </div>
      </footer>
    </main>
  );
}